<template>
  <div class="columns is-multiline">
    <div class="column">
      <span class="title is-4"><slot name="title"></slot></span>
    </div>

    <div class="column is-narrow">
      <SectionActions
        @saveDocument="saveDocument"
        @generateDocument="toggleGenerateDocumentModal"
        @exportDocument="exportDocument"
        @printDocument="printDocument"
        @previewDocument="previewDocument"
        :objectsCreationAllowed="objectsCreationAllowed"
        :exportAllowed="exportAllowed"
        :printAllowed="printAllowed"
      >
        <template v-slot:navigation>
          <slot name="navigation"></slot>
        </template>
      </SectionActions>
    </div>

    <div
      class="column text-editor"
      :class="{
        'is-7': objectsCreationAllowed,
        'is-full': !objectsCreationAllowed
      }"
    >
      <Loader v-if="isLoaded" />

      <TextEditor v-show="!isLoaded" v-bind="$props" @loaded="textEditorLoaded" ref="textEditor">
        <template v-slot:actions></template>

        <template v-if="singleIndicator && codeHandle">
          <DataObject
            v-if="singleIndicator"
            @addShortCode="addShortCode"
            @editShortCode="editShortCode"
            :singleIndicator="singleIndicator"
            :codeHandle="codeHandle"
            :codes="codes"
            :variables="variables"
            :defaultType="'chart'"
            :availableTypes="['chart']"
            class="m-b-xl"
          ></DataObject>
        </template>
      </TextEditor>
    </div>

    <div class="column is-5" v-if="objectsCreationAllowed">
      <div class="sticky">
        <SectionCodes
          :title="'Переменные'"
          :codes="variables"
          @editShortCode="toggleVariableModal"
          @deleteShortCode="showDeleteShortCodeModal"
        >
          <div class="columns">
            <div class="column">
              <b-button @click="toggleVariableModal()" icon-left="plus">Добавить</b-button>
            </div>
          </div>
        </SectionCodes>

        <SectionCodes
          :title="'Объекты данных'"
          :codes="codes"
          @editShortCode="toggleDataObjectModal"
          @deleteShortCode="showDeleteShortCodeModal"
        >
          <div class="columns">
            <div class="column">
              <b-button @click="toggleDataObjectModal()" icon-left="plus">Добавить</b-button>
            </div>
          </div>
        </SectionCodes>
      </div>
    </div>

    <b-modal :active.sync="dataObjectModal" full-screen :can-cancel="canCancel">
      <DataObjectModal
        @editShortCode="editShortCode"
        @addShortCode="addShortCode"
        :codeHandle="codeHandle"
        :codes="codes"
        :variables="variables"
      ></DataObjectModal>
    </b-modal>

    <b-modal :active.sync="variableModal" has-modal-card :can-cancel="canCancel">
      <VariableModal
        @editShortCode="editShortCode"
        @addShortCode="addShortCode"
        :codeHandle="codeHandle"
        :codes="codes"
        :variables="variables"
      />
    </b-modal>

    <b-modal :active.sync="generateDocumentModal" class="has-overflow" has-modal-card :can-cancel="canCancel">
      <GenerateDocumentModal @generateDocument="generateDocument" :templateId="$route.params.id" />
    </b-modal>

    <b-modal :active.sync="deleteShortCodeModalShown" has-modal-card can-cancel>
      <ConfirmationModal @confirm="deleteShortCode" :settings="deleteShortCodeModal" />
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  props: {
    document: {
      type: Object
    },
    exportAllowed: {
      type: Boolean,
      default: false
    },
    printAllowed: {
      type: Boolean,
      default: false
    },
    objectsCreationAllowed: {
      type: Boolean,
      default: true
    },
    storeNamespace: {
      type: String,
      default: ''
    },
    singleIndicator: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoaded: true,
      sidebarVisible: true,
      variableModal: false,
      dataObjectModal: false,
      canCancel: ['escape', 'x'],
      generateDocumentModal: false,
      deleteShortCodeModalShown: false,
      deleteShortCodeModal: {
        confirm: 'Удалить',
        cancel: 'Отменить',
        title: 'Подтвердите удаление',
        warning: 'Внимание, при удалении переменной также будут удалены связанные с ней объекты.',
        confirmButtonColor: 'is-danger'
      },
      codeHandle: null
    };
  },
  mounted() {
    if (this.singleIndicator) {
      this.codeHandle =
        this.codes.map(el => {
          const { name, ...rest } = el;

          return {
            id: '_' + el.name,
            ...rest
          };
        })[0] || {};
    } else {
      this.codeHandle = {};
    }
  },
  components: {
    TextEditor: () => import(/* webpackChunkName: "TextEditor" */ '@/components/shared/document/TextEditor'),
    SectionActions: () =>
      import(/* webpackChunkName: "SectionActions" */ '@/components/shared/document/SectionActions'),
    SectionCodes: () => import(/* webpackChunkName: "SectionCodes" */ '@/components/shared/document/SectionCodes'),
    DataObject: () => import(/* webpackChunkName: "DataObject" */ '@/components/shared/DataObject'),
    DataObjectModal: () =>
      import(/* webpackChunkName: "DataObjectModal" */ '@/components/shared/document/DataObjectModal'),
    VariableModal: () => import(/* webpackChunkName: "VariableModal" */ '@/components/shared/document/VariableModal'),
    GenerateDocumentModal: () =>
      import(/* webpackChunkName: "GenerateDocumentModal" */ '@/components/shared/document/GenerateDocumentModal'),
    ConfirmationModal: () =>
      import(/* webpackChunkName: "ConfirmationModal" */ '@/components/layout/ConfirmationModal.vue')
  },
  computed: {
    variables() {
      return this.document.data.variables || [];
    },

    codes() {
      return this.document.data.codes || [];
    }
  },
  methods: {
    ...mapActions({
      removeVariable(dispatch, payload) {
        return dispatch(this.storeNamespace + '/removeVariable', payload);
      },
      removeCode(dispatch, payload) {
        return dispatch(this.storeNamespace + '/removeCode', payload);
      },
      addVariable(dispatch, payload) {
        return dispatch(this.storeNamespace + '/addVariable', payload);
      },
      addCode(dispatch, payload) {
        return dispatch(this.storeNamespace + '/addCode', payload);
      },
      editVariable(dispatch, payload) {
        return dispatch(this.storeNamespace + '/editVariable', payload);
      },
      editCode(dispatch, payload) {
        return dispatch(this.storeNamespace + '/editCode', payload);
      }
    }),

    textEditorLoaded() {
      this.isLoaded = false;
    },

    showDeleteShortCodeModal(code) {
      this.codeHandle = code;
      this.deleteShortCodeModalShown = true;
    },

    addShortCode(ShortCode) {
      if (ShortCode.type.typeName === 'variable') {
        this.addVariable(ShortCode);
      } else {
        this.addCode(ShortCode);
      }
    },

    editShortCode(ShortCode) {
      const names = {
        oldName: this.codeHandle.id.substr(1),
        newName: ShortCode.name
      };

      const newValue = ShortCode.selectedId;

      const findShortCodeByName = ShortCode => {
        return ShortCode.name === names.oldName;
      };

      if (this.codes.some(findShortCodeByName)) {
        this.editCode({ code: ShortCode, names, newValue, target: 'self' });
      }

      if (this.variables.some(findShortCodeByName)) {
        const codesToEdit = this.codes.filter(code => {
          return code.data.series.find(serie => {
            return serie.values.find(value => {
              return value.name === names.oldName;
            });
          });
        });

        for (const code of codesToEdit) {
          this.editCode({ code, names, newValue, target: 'series' });
        }

        this.editVariable(ShortCode);
      }

      this.$root.$emit('editShortCode', names);
    },

    deleteShortCode() {
      this.deleteShortCodeModalShown = false;

      if (this.checkObjectForEmpty(this.codeHandle)) {
        return;
      }

      const ShortCodeName = this.codeHandle.id.substr(1);

      const findShortCodeByName = ShortCode => {
        return ShortCode.name === ShortCodeName;
      };

      if (this.codes.some(findShortCodeByName)) {
        this.removeCode(ShortCodeName);
        this.$root.$emit('removeShortCode', ShortCodeName);
      }

      if (this.variables.some(findShortCodeByName)) {
        const codesToRemove = this.codes.filter(code => {
          return code.data.series.find(serie => {
            return serie.values.find(value => {
              return value.name === ShortCodeName;
            });
          });
        });

        for (const code of codesToRemove) {
          this.removeCode(code.name);
          this.$root.$emit('removeShortCode', code.name);
        }

        this.removeVariable(ShortCodeName);
        this.$root.$emit('removeShortCode', ShortCodeName);
      }

      this.codeHandle = {};
    },

    toggleDataObjectModal(code = {}) {
      this.codeHandle = code;
      this.dataObjectModal = !this.dataObjectModal;
    },

    toggleVariableModal(code = {}) {
      this.codeHandle = code;
      this.variableModal = !this.variableModal;
    },

    toggleGenerateDocumentModal() {
      this.generateDocumentModal = !this.generateDocumentModal;
    },

    saveDocument() {
      this.$emit('saveDocument');
    },

    generateDocument(data) {
      this.$emit('generateDocument', data);
    },

    exportDocument(type) {
      this.$emit('exportDocument', type);
    },

    printDocument() {
      this.$emit('printDocument');
    },

    previewDocument() {
      this.$emit('previewDocument');
    }
  }
};
</script>

<style lang="scss" scoped>
.sticky {
  top: 0;
  position: sticky;
}

.text-editor {
  position: relative;
}
</style>
